// src/components/Analytics.jsx
// Componente para Google Analytics 4 y estadísticas internas
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import statsService from '../services/statsService';

// ID de medición de GA4 - Se obtiene de las variables de entorno o usa el ID por defecto
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-694BHPRXGS';

// Función para cargar el script de GA4
const loadGoogleAnalytics = () => {
  // Verificar si ya está cargado o si no hay ID configurado
  if (window.gtag || !GA_MEASUREMENT_ID) return;

  // Crear y cargar el script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Inicializar gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    // Configuraciones de privacidad
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    // Custom dimensions
    custom_map: {
      'dimension1': 'program_slug',
      'dimension2': 'unit_slug',
      'dimension3': 'content_type',
      'dimension4': 'user_type'
    },
    // Eventos de conversión
    send_page_view: true
  });
};

// Función para trackear pageviews con contexto enriquecido
const trackPageView = (path) => {
  if (window.gtag) {
    // Extraer contexto de la URL
    const pageParams = {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title
    };
    
    // Detectar tipo de página y agregar custom dimensions
    if (path.startsWith('/p/')) {
      const parts = path.split('/');
      const programSlug = parts[2];
      pageParams.content_type = 'program';
      pageParams.program_slug = programSlug;
      
      // Si es una unidad específica
      if (parts.length > 4 && parts[3] === 'u') {
        const unitSlug = parts[4];
        pageParams.content_type = 'unit';
        pageParams.unit_slug = unitSlug;
      }
    } else if (path === '/programas') {
      pageParams.content_type = 'programs_index';
    }
    
    window.gtag('config', GA_MEASUREMENT_ID, pageParams);
  }
};

// Componente principal
const Analytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Cargar GA4 solo en producción
    if (process.env.NODE_ENV === 'production') {
      loadGoogleAnalytics();
    }
  }, []);

  useEffect(() => {
    // Trackear cambios de página en GA4
    if (process.env.NODE_ENV === 'production') {
      trackPageView(location.pathname);
    }

    // Registrar visita en nuestras estadísticas internas (siempre)
    statsService.recordPageVisit(location.pathname);
  }, [location]);

  return null; // Este componente no renderiza nada visual
};

// Hook personalizado para eventos personalizados
export const useAnalytics = () => {
  const trackEvent = (eventName, parameters = {}) => {
    if (process.env.NODE_ENV === 'production' && window.gtag) {
      window.gtag('event', eventName, {
        event_category: 'engagement',
        event_label: parameters.label || '',
        ...parameters
      });
    }
  };

  const trackButtonClick = (buttonName) => {
    trackEvent('button_click', {
      event_category: 'ui_interaction',
      button_name: buttonName
    });
  };

  const trackLinkClick = (linkUrl, linkText) => {
    trackEvent('link_click', {
      event_category: 'navigation',
      link_url: linkUrl,
      link_text: linkText
    });
  };

  return {
    trackEvent,
    trackButtonClick,
    trackLinkClick
  };
};

export default Analytics;